import { getJobStatus } from '~api/ai/getJobStatus';
import type { AiJobStatusResponse } from '~api/ai/getJobStatus';

import { ExtractQuestionsEventAdapter } from '~analytics/adapters/ExtractQuestionsEventAdapter';
import type { Poll, PollConfig, PollResult } from '~shared/polling';
import { PollStatus } from '~shared/polling';

interface ExtractQuestionsMetadata {
  requestId: string;
  quizId: number;
  socNumber?: string;
  workspaceId?: string;
  workspaceQuizId?: string;
  analyticsMetadata?: {
    fileUploaded: boolean;
    fileUploadType: string | null;
    fileUploadSizeBytes: number | null;
    fileUploadMaxLimitReached: boolean;
    filesUploadedCount: number;
    includeExplanations: boolean;
  };
}

export const extractQuestionsPollerConfig: PollConfig<
  AiJobStatusResponse,
  ExtractQuestionsMetadata
> = {
  type: 'ai-extract-questions',

  pollFn: async (
    poll: Poll<AiJobStatusResponse, ExtractQuestionsMetadata>
  ): Promise<PollResult<AiJobStatusResponse>> => {
    const { requestId } = poll.metadata as unknown as ExtractQuestionsMetadata;

    const jobStatus = await getJobStatus(requestId);

    if (jobStatus.status === 'completed') {
      return {
        status: PollStatus.Completed,
        data: jobStatus,
        shouldContinuePolling: false,
      };
    } else if (jobStatus.status === 'failed') {
      return {
        status: PollStatus.Failed,
        data: jobStatus,
        shouldContinuePolling: false,
      };
    } else {
      return {
        status: PollStatus.Pending,
        data: jobStatus,
        shouldContinuePolling: true,
      };
    }
  },

  onComplete: (poll, result) => {
    console.debug('[ExtractQuestionsPoller] Polling completed', poll, result);

    const { analyticsMetadata } =
      poll.metadata as unknown as ExtractQuestionsMetadata;

    // Send analytics for async responses
    if (analyticsMetadata) {
      const numQuestions = result.data?.data?.questionCount;
      console.debug('[ExtractQuestionsPoller] Sending analytics');
      new ExtractQuestionsEventAdapter({
        ...analyticsMetadata,
        numQuestions,
      }).sendAnalytics();
    }
  },

  onFailure: (poll, error) => {
    console.debug('[ExtractQuestionsPoller] Polling failed', poll, error);
  },

  onTimeout: (poll) => {
    console.debug('[ExtractQuestionsPoller] Polling timed out', poll);
  },

  pollingStrategy: {
    initialDelayMs: 5000,
    fastPollingInterval: 5000,
    fastPollingDuration: 2 * 60 * 1000,
    maxInterval: 30000,
    maxDuration: 7 * 60 * 1000,
    initialInterval: 5000,
    useExponentialBackoff: false,
  },
};
